// Local replacements for the three lodash helpers this app used. Lodash was
// pulled in for `debounce`, `throttle` and `memoize` alone, and ships as CJS
// so bundlers cannot tree-shake the rest of it away.
//
// Each of these keeps the lodash default behaviour the call sites relied on,
// and nothing more - these are not general-purpose ports. In particular none
// of them forwards `this`, because every call site uses arrow functions.

type Timer = ReturnType<typeof setTimeout>

/** A deferred function that can be cancelled before it runs. */
export type Cancellable<TArgs extends unknown[]> = ((
  ...args: TArgs
) => void) & {
  cancel: () => void
}

/**
 * Delay `fn` until `wait` ms have passed without another call. Trailing edge
 * only, matching lodash's default (`leading: false, trailing: true`).
 *
 * Returns `void` rather than the last result, which lodash does return. That
 * matters for react-select: its async loader calls `loadOptions(input, cb)`
 * and *also* chains `.then(cb)` onto any thenable the loader returns, so
 * handing back a stale promise from a previous invocation would deliver a
 * second, out-of-date set of results.
 *
 * Call sites must keep the returned function across renders (`useMemo` or a
 * ref). Rebuilding it every render throws away the pending timer, which
 * silently disables debouncing altogether.
 */
export const debounce = <TArgs extends unknown[]>(
  fn: (...args: TArgs) => unknown,
  wait: number,
): Cancellable<TArgs> => {
  let timer: Timer | undefined

  const debounced = (...args: TArgs): void => {
    if (timer !== undefined) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = undefined
      fn(...args)
    }, wait)
  }

  debounced.cancel = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
  }

  return debounced
}

/**
 * Run `fn` at most once per `wait` ms. Fires immediately on the first call and
 * once more after a burst ends, matching lodash's defaults (`leading: true,
 * trailing: true`) - the trailing call is what leaves a scroll handler on the
 * final position rather than one frame short of it.
 */
export const throttle = <TArgs extends unknown[]>(
  fn: (...args: TArgs) => unknown,
  wait: number,
): Cancellable<TArgs> => {
  let lastRun = 0
  let timer: Timer | undefined
  let pendingArgs: TArgs | undefined

  const run = (args: TArgs) => {
    lastRun = Date.now()
    pendingArgs = undefined
    fn(...args)
  }

  const throttled = (...args: TArgs): void => {
    const remaining = wait - (Date.now() - lastRun)
    pendingArgs = args

    if (remaining <= 0) {
      if (timer !== undefined) {
        clearTimeout(timer)
        timer = undefined
      }
      run(args)
    } else if (timer === undefined) {
      timer = setTimeout(() => {
        timer = undefined
        if (pendingArgs !== undefined) run(pendingArgs)
      }, remaining)
    }
  }

  throttled.cancel = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
    pendingArgs = undefined
  }

  return throttled
}

/**
 * Cache `fn`'s result per argument, keyed by the argument itself - the same
 * contract as lodash's single-argument `memoize`. The cache is unbounded and
 * never evicts, which is why this is restricted to a single key-like argument.
 */
export const memoize = <TArg, TResult>(
  fn: (arg: TArg) => TResult,
): ((arg: TArg) => TResult) => {
  const cache = new Map<TArg, TResult>()

  return (arg: TArg): TResult => {
    if (cache.has(arg)) return cache.get(arg) as TResult
    const result = fn(arg)
    cache.set(arg, result)
    return result
  }
}
